import type { AnalyticsProvider } from '~analytics/types/analyticsProvider';

import GoogleAnalyticsProvider from '~analytics/providers/GoogleAnalyticsProvider';
import GoogleTagManagerProvider from '~analytics/providers/GoogleTagManagerProvider';
import HotjarProvider from '~analytics/providers/HotJarProvider';
import MetaPixelProvider from '~analytics/providers/MetaPixelProvider';
import MixpanelProvider from '~analytics/providers/MixpanelProvider';
import MockProvider from '~analytics/providers/MockProvider';
import TikTokPixelProvider from '~analytics/providers/TikTokProvider';
import { storeToRefs } from 'pinia';

import { GTM_EVENT_MAP } from '~/constants/gtmEvents';
import { useConfigStore } from '~/stores/config';
import { resolveAllowedAnalyticsProviders } from '~/utils/analytics/allowedAnalyticsProviders';
import { isAnywayEmail } from '~/utils/anywayEmail';

export default defineNuxtPlugin({
  name: 'analytics-service',
  setup: async () => {
    const analytics = useAnalytics();

    const config = useRuntimeConfig();

    // filter our register, mixpanel does this on the backend now
    const allEvents: string[] = Object.keys(EVENTS)
      .map(eventKey => EVENTS[<keyof typeof EVENTS>eventKey])
      .filter(event => event !== EVENTS.ACCOUNT_REGISTER_COMPLETED && event !== EVENTS.LEARN_TOPIC_COMPLETED);

    if (import.meta.client) {
      const { $logWarnMessage } = useNuxtApp();
      const { getFlag, getVariant } = useFeatureFlags();
      const enableCustomFlushInterval = await getFlag('mixpanel-batch-flush-interval');

      let batchFlushIntervalMs = 5000; // default intervals
      if (enableCustomFlushInterval) {
        const flagVariant = await getVariant('mixpanel-batch-flush-interval');
        if (flagVariant.name === 'interval') {
          batchFlushIntervalMs = flagVariant.payload?.value ? Number.parseInt(flagVariant.payload.value, 10) : 5000;
        }
      }

      // Registration loads a provider's third-party script, so the browser registers
      // only the providers the backend controls service authorises for the current
      // cluster (schools clusters suppress the Analytics and Marketing tiers). The
      // client holds no tiering policy — consent decisions can move server-side
      // (school/district) without changing this plugin. Cluster switching is always a
      // full document load, so reading the allow-list once here is sufficient.
      //
      // Trust the config-store cluster only when its code matches the authoritatively
      // resolved cluster code: an unresolved host (missing/unknown X-Cluster-Code)
      // leaves the config store defaulted to clusters[0], a consumer cluster with a
      // full allow-list — registering those on a schools host would defeat the gate.
      // A mismatch yields an empty allow-list, so registration fails closed.
      const { code } = useCurrentCluster();
      const allowedProviders = resolveAllowedAnalyticsProviders(code.value, useConfigStore().cluster);
      if (allowedProviders.size === 0) {
        $logWarnMessage('[analytics] cluster unresolved — no analytics providers will register');
      }

      // Every provider's init is synchronous, so these settle in microtasks. Braze is the one
      // real wait (its chunk loads over the network) and is held apart so nothing below blocks on it.
      const providerRegistrations: Promise<void>[] = [];
      let brazeRegistration: Promise<void> | undefined;
      const register = (provider: AnalyticsProvider) => {
        if (!allowedProviders.has(provider.serviceName)) {
          return;
        }
        providerRegistrations.push(analytics.registerProvider(provider));
      };

      if (config.public.analyticsEnabled) {
        // The Braze SDK is large, so it loads as its own chunk, only when analytics is on,
        // and registers without holding up the other providers.
        if (allowedProviders.has('braze')) {
          brazeRegistration = import('~analytics/providers/BrazeProvider').then(({ default: BrazeProvider }) =>
            analytics.registerProvider(new BrazeProvider('braze', config, [
              EVENTS.PHOENIX_REFERRAL_CODE_COPIED,
              EVENTS.UNIFIED_CONTENT_COLLECTION_COMPLETED,
              EVENTS.UNIFIED_CONTENT_COLLECTION_ASSET_SELECTED,
              EVENTS.UNIFIED_CONTENT_COLLECTION_STARTED,
              EVENTS.UNIFIED_CONTENT_ASSET_COMPLETED,
            ])),
          );
        }

        register(new MixpanelProvider('mixpanel', config, allEvents, enableCustomFlushInterval, batchFlushIntervalMs));
        register(new GoogleTagManagerProvider('google-tag-manager', GTM_EVENT_MAP));
        register(new GoogleAnalyticsProvider('google-analytics', config, [EVENTS.ACCOUNT_REGISTER_COMPLETED, EVENTS.LEARN_TOPIC_COMPLETED]));
        register(new MetaPixelProvider('meta-pixel', config, [EVENTS.ACCOUNT_REGISTER_COMPLETED, EVENTS.LEARN_TOPIC_COMPLETED, EVENTS.UNIFIED_STORY_COMPLETED]));
        register(new HotjarProvider('hotjar', config, []));
        register(new TikTokPixelProvider('tiktok-pixel', config, [EVENTS.ACCOUNT_REGISTER_COMPLETED, EVENTS.LEARN_TOPIC_COMPLETED]));
      }
      else {
        register(new MockProvider('braze', config, [
          EVENTS.PHOENIX_REFERRAL_CODE_COPIED,
          EVENTS.UNIFIED_CONTENT_COLLECTION_COMPLETED,
          EVENTS.UNIFIED_CONTENT_COLLECTION_ASSET_SELECTED,
          EVENTS.UNIFIED_CONTENT_COLLECTION_STARTED,
          EVENTS.UNIFIED_CONTENT_ASSET_COMPLETED,
        ]));

        register(new MockProvider('mixpanel', config, allEvents));
        register(new MockProvider('google-tag-manager', config, Object.keys(GTM_EVENT_MAP)));
        register(new MockProvider('google-analytics', config, [EVENTS.ACCOUNT_REGISTER_COMPLETED, EVENTS.LEARN_TOPIC_COMPLETED]));
        register(new MockProvider('meta-pixel', config, [EVENTS.ACCOUNT_REGISTER_COMPLETED, EVENTS.LEARN_TOPIC_COMPLETED, EVENTS.UNIFIED_STORY_COMPLETED]));
        register(new MockProvider('hotjar', config, []));
        register(new MockProvider('tiktok-pixel', config, [EVENTS.ACCOUNT_REGISTER_COMPLETED, EVENTS.LEARN_TOPIC_COMPLETED]));
      }

      const { user } = storeToRefs(useAuthStore());

      // The urql exchange resets the identity when a request finds the session expired, but a
      // boot-time user fetch runs before this plugin provides $analytics, so the bounce to
      // `?sessionExpired=true` is where that reset lands. It runs here, before the app mounts,
      // so no login can race it; a reload of that URL on a live session hydrates a user and
      // keeps the identity.
      if (useRoute().query.sessionExpired && !user.value) {
        await Promise.allSettled(providerRegistrations);
        analytics.reset();
      }

      const syncAnywayUser = (email: string | null | undefined) => {
        analytics.setUserProperties({ isAnywayUser: isAnywayEmail(email) });
      };

      watch(() => user.value?.primaryEmail, syncAnywayUser);
      void Promise.allSettled([...providerRegistrations, brazeRegistration]).then(() => syncAnywayUser(user.value?.primaryEmail));
    }

    return { provide: { analytics } };
  },
});
